/**
 * model-card-grid-lib.ts — Phần THUẦN của lưới thẻ model dùng chung (`model-card-grid.tsx`): màn Nguồn AI và lượt AI
 * (bước 2 "Chọn model của tổ chức") và mục Bộ não của nhân viên AI ("Dùng model khác cho nhân viên này").
 *
 *  - tag của một thẻ: nhà cung cấp (ChatGPT/Claude/Gemini/Grok/DeepSeek), hạng (Tiết kiệm / Tiêu chuẩn / Cao cấp), với
 *    AI của eCDP "≈ N lượt / câu trả lời", với Key của tôi trạng thái key của nhà cung cấp ("Có key" / "Cần key" / "Key lỗi");
 *    model khoá theo gói → 🔒 "Cần gói trả phí";
 *  - thứ tự thẻ: theo nhà cung cấp (ChatGPT, Claude, Gemini, Grok, DeepSeek), trong một nhà cung cấp giữ thứ tự danh mục;
 *  - nút "Dùng model này" bật / tắt (đang dùng, khoá theo gói, Key của tôi chưa có key hợp lệ).
 */
import { SUPPORTED_AI_PROVIDERS } from '@/lib/ai-providers'
import { i18n } from '@/lib/i18n'
import {
  byokBroken, formatTurns, lockText, perReplyText, turnsPerReply, type AiModelEntry, type AiSource, type BadgeTone, type ByokState,
} from '@/lib/ai-credits'

export interface ModelRef {
  provider: string | null
  model: string | null
}

/** Tên hiển thị nhà cung cấp — dự phòng khi chưa có `GET /ai/sources`. */
export const PROVIDER_LABEL: Record<string, string> = {
  openai: 'ChatGPT', anthropic: 'Claude', gemini: 'Gemini', xai: 'Grok', deepseek: 'DeepSeek',
}

export function providerLabel(id: string, names?: Array<{ provider: string; name: string }> | null): string {
  return names?.find((n) => n.provider === id)?.name ?? PROVIDER_LABEL[id] ?? id
}

export const TIER_LABEL: Record<string, string> = {
  get economy() { return i18n.t('aiComp:modelCard.tier.economy') },
  get standard() { return i18n.t('aiComp:modelCard.tier.standard') },
  get premium() { return i18n.t('aiComp:modelCard.tier.premium') },
}
export const tierLabel = (tier: string | null | undefined): string | null => (tier ? TIER_LABEL[tier] ?? tier : null)

/** Mô tả ngắn theo hạng (khung "Cài đặt model"). */
export const TIER_DESC: Record<string, string> = {
  get economy() { return i18n.t('aiComp:modelCard.tierDesc.economy') },
  get standard() { return i18n.t('aiComp:modelCard.tierDesc.standard') },
  get premium() { return i18n.t('aiComp:modelCard.tierDesc.premium') },
}

/** Nhân viên AI không đặt model riêng (nền tảng + model trống) → theo model của tổ chức. */
export const inheritsOrgModel = (m: ModelRef | null | undefined): boolean => !m?.provider && !m?.model

export const modelKey = (m: ModelRef | null | undefined): string => (m?.provider && m?.model ? `${m.provider}:${m.model}` : '')
export const sameModel = (a: ModelRef | null | undefined, b: ModelRef | null | undefined): boolean =>
  !!modelKey(a) && modelKey(a) === modelKey(b)

/** Khoá vì gói (mời nâng gói) — lý do khác (chưa mở bán) thì không. */
export const lockedByPlan = (m: Pick<AiModelEntry, 'allowed' | 'lockReason'>): boolean =>
  !m.allowed && /gói/i.test(m.lockReason ?? 'gói') // i18n-ignore — đối chiếu lý do khoá do máy chủ trả (tiếng Việt)

/** Nhãn khoá trên thẻ: khoá theo gói → "Cần gói trả phí"; khác → lý do của máy chủ; dùng được → null. */
export function lockLabel(m: Pick<AiModelEntry, 'allowed' | 'lockReason'>): string | null {
  if (m.allowed) return null
  return lockedByPlan(m) ? i18n.t('aiComp:modelCard.needPaidPlan') : lockText(m)
}

/** Giá trị định danh (so sánh ở nơi khác) — chữ hiển thị lấy qua keyTagText(). */
export type KeyTagLabel = 'Có key' | 'Cần key' | 'Key lỗi' // i18n-ignore

/** Nhãn hiển thị (đa ngôn ngữ) của một KeyTagLabel. */
export function keyTagText(label: KeyTagLabel): string {
  if (label === 'Cần key') return i18n.t('aiComp:modelCard.key.need') // i18n-ignore
  if (label === 'Key lỗi') return i18n.t('aiComp:modelCard.key.broken') // i18n-ignore
  return i18n.t('aiComp:modelCard.key.ok')
}

/** Trạng thái key của nhà cung cấp (Key của tôi). */
export function keyTag(byok: ByokState | null | undefined): { label: KeyTagLabel; tone: BadgeTone } {
  if (!byok?.configured) return { label: 'Cần key', tone: 'warning' } // i18n-ignore
  if (byokBroken(byok)) return { label: 'Key lỗi', tone: 'destructive' } // i18n-ignore
  return { label: 'Có key', tone: 'success' } // i18n-ignore
}

/** Key hợp lệ (đã lưu, không lỗi). */
export const keyUsable = (byok: ByokState | null | undefined): boolean => keyTag(byok).label === 'Có key' // i18n-ignore

export interface CardTag {
  /** credit = ≈ lượt AI / câu trả lời; wallet = (cũ, không còn dùng). */
  kind: 'provider' | 'tier' | 'credit' | 'wallet' | 'key'
  label: string
  tone: BadgeTone
}

export interface ModelCardView {
  key: string
  provider: string
  model: string
  title: string
  source: AiSource
  tags: CardTag[]
  /** 🔒 lý do khoá (null = dùng được). */
  lock: string | null
  /** Khoá theo gói → hiện "Nâng gói". */
  planLocked: boolean
  current: boolean
}

/**
 * Một thẻ model. `byokOf(provider)` = trạng thái key của nhà cung cấp (chỉ dùng cho Key của tôi);
 * `current` = model đang dùng (thẻ có nhãn "Đang dùng").
 */
export function modelCardView(
  m: AiModelEntry,
  ctx: {
    byokOf?: (provider: string) => ByokState | null | undefined
    current?: ModelRef | null
    names?: Array<{ provider: string; name: string }> | null
  } = {},
): ModelCardView {
  const tags: CardTag[] = [{ kind: 'provider', label: providerLabel(m.provider, ctx.names), tone: 'outline' }]
  const tier = tierLabel(m.tier)
  if (tier) tags.push({ kind: 'tier', label: tier, tone: 'secondary' })
  if (m.source === 'platform') {
    const per = perReplyText(m)
    if (per) tags.push({ kind: 'credit', label: per, tone: 'default' })
  } else {
    const k = keyTag(ctx.byokOf?.(m.provider))
    tags.push({ kind: 'key', label: keyTagText(k.label), tone: k.tone })
  }
  return {
    key: modelKey(m), provider: m.provider, model: m.model, title: m.title, source: m.source, tags,
    lock: lockLabel(m), planLocked: lockedByPlan(m), current: sameModel(m, ctx.current),
  }
}

const ORDER = SUPPORTED_AI_PROVIDERS as readonly string[]
const rank = (id: string) => (ORDER.includes(id) ? ORDER.indexOf(id) : ORDER.length)

/**
 * Model của MỘT nguồn, xếp theo nhà cung cấp (ChatGPT, Claude, Gemini, Grok, DeepSeek; lạ xếp sau), trong nhà cung cấp
 * giữ thứ tự danh mục. Máy chủ tắt chế độ AI của eCDP thì `?source=platform` trả model Key của tôi → lọc bỏ.
 */
export function orderModels(models: AiModelEntry[] | null | undefined, source?: AiSource | null): AiModelEntry[] {
  const list = (models ?? []).filter((m) => !source || m.source === source)
  return list
    .map((m, i) => ({ m, i }))
    .sort((a, b) => rank(a.m.provider) - rank(b.m.provider) || a.m.provider.localeCompare(b.m.provider) || a.i - b.i)
    .map((x) => x.m)
}

/**
 * Nút "Dùng model này" (khung Cài đặt model): bật khi model dùng được với gói và — Key của tôi — nhà cung cấp đã có key
 * hợp lệ; đang dùng rồi thì tắt. `reason` = chữ giải thích khi tắt.
 */
export function modelDecision(input: {
  model: AiModelEntry | null | undefined
  byok?: ByokState | null
  current?: ModelRef | null
  providerName?: string
}): { enabled: boolean; reason: string | null; isCurrent: boolean } {
  const m = input.model
  if (!m) return { enabled: false, reason: i18n.t('aiComp:modelCard.pickOne'), isCurrent: false }
  if (sameModel(m, input.current)) return { enabled: false, reason: i18n.t('aiComp:modelCard.orgUsing'), isCurrent: true }
  const lock = lockLabel(m)
  if (lock) return { enabled: false, reason: lockedByPlan(m) ? i18n.t('aiComp:modelCard.needPaidUpgrade') : lock, isCurrent: false }
  if (m.source === 'byok') {
    const name = input.providerName ?? providerLabel(m.provider)
    const k = keyTag(input.byok)
    if (k.label === 'Cần key') return { enabled: false, reason: i18n.t('aiComp:modelCard.saveKeyFirst', { name }), isCurrent: false } // i18n-ignore
    if (k.label === 'Key lỗi') return { enabled: false, reason: i18n.t('aiComp:modelCard.keyBrokenFirst', { name }), isCurrent: false } // i18n-ignore
  }
  return { enabled: true, reason: null, isCurrent: false }
}

// ── Thẻ NHÀ CUNG CẤP (tầng 1 của lưới) ──────────────────────────────────────

export interface ProviderCardView {
  provider: string
  name: string
  /** Số model dùng được (không khoá) / tổng số model của nhà cung cấp trong nguồn này. */
  usable: number
  total: number
  tags: CardTag[]
  /** Model đang dùng thuộc nhà cung cấp này. */
  current: boolean
}

/** Nhóm model (đã xếp bằng orderModels) theo nhà cung cấp, giữ thứ tự. */
export function groupByProvider(models: AiModelEntry[]): Array<{ provider: string; models: AiModelEntry[] }> {
  const out: Array<{ provider: string; models: AiModelEntry[] }> = []
  for (const m of models) {
    const g = out.find((x) => x.provider === m.provider)
    if (g) g.models.push(m)
    else out.push({ provider: m.provider, models: [m] })
  }
  return out
}

/** Thẻ một nhà cung cấp: tên, "N model", eCDP "từ ≈ N lượt / câu trả lời" (model rẻ nhất dùng được) hoặc trạng thái key. */
export function providerCardView(
  provider: string,
  models: AiModelEntry[],
  ctx: {
    byokOf?: (provider: string) => ByokState | null | undefined
    current?: ModelRef | null
    names?: Array<{ provider: string; name: string }> | null
  } = {},
): ProviderCardView {
  const usableList = models.filter((m) => !lockLabel(m))
  const tags: CardTag[] = [{ kind: 'provider', label: i18n.t('aiComp:modelCard.modelCount', { count: models.length }), tone: 'outline' }]
  const source = models[0]?.source
  if (source === 'platform') {
    const turns = usableList.map((m) => turnsPerReply(m)).filter((c): c is number => c != null)
    if (turns.length) tags.push({ kind: 'credit', label: i18n.t('aiComp:modelCard.fromTurns', { turns: formatTurns(Math.min(...turns), i18n.t('aiComp:modelCard.turnSuffix')) }), tone: 'default' })
    if (!usableList.length && models.some(lockedByPlan)) tags.push({ kind: 'tier', label: i18n.t('aiComp:modelCard.needPaidPlan'), tone: 'warning' })
  } else if (source === 'byok') {
    const k = keyTag(ctx.byokOf?.(provider))
    tags.push({ kind: 'key', label: keyTagText(k.label), tone: k.tone })
  }
  return {
    provider, name: providerLabel(provider, ctx.names), usable: usableList.length, total: models.length, tags,
    current: !!ctx.current?.provider && ctx.current.provider === provider && models.some((m) => sameModel(m, ctx.current)),
  }
}

/** Nhà cung cấp mở sẵn: của model đang chọn → của model đang dùng → nhà cung cấp đầu tiên. */
export function initialProvider(
  groups: Array<{ provider: string }>,
  selected?: ModelRef | null,
  current?: ModelRef | null,
): string | null {
  const has = (p?: string | null) => !!p && groups.some((g) => g.provider === p)
  if (has(selected?.provider)) return selected!.provider!
  if (has(current?.provider)) return current!.provider!
  return groups[0]?.provider ?? null
}

/**
 * Nhãn một dòng trong ô chọn model: "GPT-5.4 Mini · Tiết kiệm · ≈ 1 lượt / câu trả lời · đang dùng" / "… · 🔒 Cần gói trả phí".
 */
export function modelOptionLabel(v: Pick<ModelCardView, 'title' | 'tags' | 'lock' | 'current'>, currentLabel = i18n.t('aiComp:modelCard.inUse')): string {
  const parts = [v.title, ...v.tags.filter((t) => t.kind === 'tier' || t.kind === 'credit' || t.kind === 'wallet').map((t) => t.label)]
  if (v.lock) parts.push(`🔒 ${v.lock}`)
  if (v.current) parts.push(currentLabel)
  return parts.join(' · ')
}
